// Chat screen: renders history + live messages for the open room, sends
// signed+encrypted messages, and shows the local Ed25519 identity
// fingerprint (for out-of-band comparison - see README threat model). The
// identity itself is derived from the account password (crypto/identity.ts)
// so there's no export/import step to move between devices.
//
// Rendering pipeline per message, strictly in this order:
//   1. Resolve the sender's Ed25519 public key (REST + trust-on-first-use
//      pin, see store/vault.ts::checkAndPinPeer). A pin mismatch is treated
//      as untrusted - the message is never decrypted, only flagged.
//   2. Verify the signature over the exact canonical string the server
//      checked (roomId|sender|ts|iv|ct|kind). Anything that fails is
//      rendered as a visibly invalid row and never decrypted.
//   3. Only then decrypt with the local room key (H1) and render - text via
//      textContent (never innerHTML, which is what made the original
//      index.html vulnerable to a stored-XSS via message text), images via
//      an <img> whose src is an object URL we created ourselves from
//      decrypted bytes, never a user-controlled string.
//
// A message is either text or an image (LoadedMessage.kind), sharing every
// other guarantee: an image's `ct` is the sha256 of its (separately
// fetched) ciphertext blob, so it's covered by the same signature as text -
// see server/src/messages.ts for why that means no new crypto invariant is
// needed for tamper-evidence.
//
// Message data vs. DOM are deliberately decoupled: `allMessages` holds
// every message fetched for the currently open room (ascending by
// serverTs), each resolved (verified + decrypted) at most once and cached
// on the entry itself. The DOM only ever holds a contiguous window
// [windowStart, windowEnd) of that array, capped at MAX_RENDERED rows -
// scrolling toward either edge grows the window (from memory if already
// loaded, from the network only when paging past what's been fetched so
// far), and growing past the cap trims from whichever edge is furthest
// from the viewport. This keeps a long-lived room from turning into an
// ever-growing DOM while still letting the user page back through history.
import { aesEncrypt, aesDecrypt, aesEncryptBytes, aesDecryptBytes, importAesKey, type EncBlob } from '../crypto/aes.js';
import { signMessage, verifyMessage, fingerprint, fingerprintFromRawB64 } from '../crypto/identity.js';
import { prepareImage } from '../media/imagePrep.js';
import * as api from '../net/rest.js';
import type { InboundMsgNew, InboundError } from '../net/ws.js';
import { checkAndPinPeer, listRoomsLocal, type StoredRoom } from '../store/vault.js';
import { showRoomsScreen } from './screens.js';
import { bindPowProgress } from './pow-progress.js';
import { state } from '../state.js';

function el<T extends HTMLElement>(id: string): T {
  return document.getElementById(id) as T;
}

// How many messages a single history fetch pulls, the hard cap on rendered
// DOM rows, the count trimming settles at once that cap is exceeded, and
// how close to an edge (px) triggers loading the next page in that
// direction.
const FETCH_LIMIT = 100;
const MAX_RENDERED = 500;
const TRIM_TO = 400;
const SCROLL_TRIGGER_PX = 150;

/** Result of resolveMessage(): signature verified, and for a valid message, decrypted. */
type Resolved =
  | { kind: 'text'; valid: boolean; text: string }
  | { kind: 'image'; valid: boolean; imageUrl?: string; width: number; height: number; text?: string };

interface LoadedMessage {
  id: string;
  roomId: string;
  sender: string;
  kind: 'text' | 'image';
  iv: string;
  ct: string; // text: AES-GCM ciphertext (base64). image: the referenced blob's id.
  // Image-only, unsigned/cosmetic - a layout hint, never trusted for anything security-relevant.
  width?: number;
  height?: number;
  sizeBytes?: number;
  ts: number;
  sig: string;
  serverTs: number;
  /** Verified/decrypted exactly once, then cached here for as long as the message stays in memory. */
  resolved?: Resolved;
  /** The rendered DOM row, kept so a later grouping change (see trimWindow) can toggle it without a full rebuild. */
  row?: HTMLElement;
}

function toLoadedMessage(m: api.HistoryMessage | InboundMsgNew): LoadedMessage {
  return {
    id: m.id,
    roomId: m.roomId,
    sender: m.sender,
    kind: m.kind,
    iv: m.iv,
    ct: m.ct,
    width: m.width,
    height: m.height,
    sizeBytes: m.sizeBytes,
    ts: m.ts,
    sig: m.sig,
    serverTs: m.serverTs,
  };
}

export interface ChatController {
  openRoom: (room: StoredRoom) => Promise<void>;
  handleIncomingMessage: (m: InboundMsgNew) => void;
  handleWsError: (err: InboundError) => void;
}

export function wireChatScreen(): ChatController {
  const chatHeaderTitle = el<HTMLElement>('chat-header-title');
  const messagesBox = el<HTMLElement>('messages-box');
  const messageInput = el<HTMLInputElement>('message-input');
  const backBtn = el<HTMLElement>('back-to-rooms');
  const channelList = el<HTMLElement>('channel-list');
  const userDisplay = el<HTMLElement>('logged-user-display');
  const logoutBtn = el<HTMLButtonElement>('chat-logout-btn');
  const fpEl = el<HTMLElement>('identity-fingerprint');
  const sidebar = el<HTMLElement>('chat-sidebar');
  const sidebarBackdrop = el<HTMLElement>('sidebar-backdrop');
  const menuToggleBtn = el<HTMLButtonElement>('menu-toggle-btn');
  const attachBtn = el<HTMLButtonElement>('attach-image-btn');
  const attachInput = el<HTMLInputElement>('attach-image-input');
  const imageProgress = bindPowProgress('chat-image-progress', 'chat-image-pow-label', 'chat-image-pow-fill');

  // Off-canvas sidebar drawer, only actually visible below the 768px
  // breakpoint (see index.html's @media block) - on desktop the sidebar is
  // always a static column and these classes are simply inert.
  function openDrawer(): void {
    sidebar.classList.add('open');
    sidebarBackdrop.classList.add('open');
  }
  function closeDrawer(): void {
    sidebar.classList.remove('open');
    sidebarBackdrop.classList.remove('open');
  }
  menuToggleBtn.addEventListener('click', openDrawer);
  sidebarBackdrop.addEventListener('click', closeDrawer);

  let currentAesKey: CryptoKey | null = null;

  // Per-room message state, reset on every openRoom() call - see the file
  // header for the data/DOM split this implements.
  let allMessages: LoadedMessage[] = [];
  let knownIds = new Set<string>();
  let windowStart = 0; // inclusive index into allMessages currently rendered
  let windowEnd = 0; // exclusive index into allMessages currently rendered
  let historyExhausted = false; // true once a page came back shorter than FETCH_LIMIT
  let loadingOlder = false;
  let loadingNewer = false;

  async function refreshIdentityPanel(): Promise<void> {
    if (!state.identity) return;
    fpEl.textContent = await fingerprint(state.identity.publicKey);
  }

  async function renderChannelList(activeRoomId: string): Promise<void> {
    const rooms = state.username ? await listRoomsLocal(state.username) : [];
    channelList.innerHTML = '';
    for (const room of rooms) {
      const div = document.createElement('div');
      div.className = 'channel' + (room.roomId === activeRoomId ? ' active' : '');
      div.textContent = room.name; // textContent, not innerHTML
      div.addEventListener('click', () => {
        void openRoom(room);
      });
      channelList.appendChild(div);
    }
  }

  async function getSignerKey(username: string): Promise<{ edPubB64: string; mismatch: boolean }> {
    const cached = state.peerKeys.get(username);
    if (cached) return cached;

    const { edPubB64 } = await api.getUserKey(username);
    const fp = await fingerprintFromRawB64(edPubB64);
    const pin = await checkAndPinPeer(username, edPubB64, fp);

    const info = { edPubB64, fingerprint: fp, mismatch: pin.status === 'mismatch' };
    state.peerKeys.set(username, info);

    if (pin.status === 'mismatch') {
      // Deliberately does not overwrite the pin automatically - see
      // store/vault.ts::checkAndPinPeer. Surface it and let messages from
      // this sender render as untrusted until a human resolves it.
      console.warn(
        `SECURITY WARNING: ${username}'s signing key changed since it was first seen. ` +
          `Pinned fingerprint: ${pin.pinned.fingerprint}, new fingerprint: ${fp}. ` +
          'Messages from them are shown as unverified.',
      );
    }
    return info;
  }

  /** Verifies + decrypts a message exactly once; later calls return the cached result. */
  async function resolveMessage(m: LoadedMessage): Promise<Resolved> {
    if (m.resolved) return m.resolved;

    const canonical = `${m.roomId}|${m.sender}|${m.ts}|${m.iv}|${m.ct}|${m.kind}`;
    const keyInfo = await getSignerKey(m.sender);
    const valid = !keyInfo.mismatch && (await verifyMessage(keyInfo.edPubB64, canonical, m.sig));
    const width = m.width ?? 0;
    const height = m.height ?? 0;

    let resolved: Resolved;
    if (!valid) {
      const text = keyInfo.mismatch
        ? "[hidden: sender's signing key changed and is not yet re-verified]"
        : '[rejected: invalid signature]';
      resolved = m.kind === 'image' ? { kind: 'image', valid: false, text, width, height } : { kind: 'text', valid: false, text };
    } else if (m.kind === 'image') {
      if (currentAesKey) {
        try {
          const ctBytes = await api.fetchImage(m.ct);
          const bytes = await aesDecryptBytes(currentAesKey, m.iv, ctBytes);
          const imageUrl = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'image/jpeg' }));
          resolved = { kind: 'image', valid: true, imageUrl, width, height };
        } catch {
          resolved = { kind: 'image', valid: true, text: '[failed to load image - wrong room key?]', width, height };
        }
      } else {
        resolved = { kind: 'image', valid: true, width, height };
      }
    } else if (currentAesKey) {
      try {
        const plaintext = await aesDecrypt(currentAesKey, { iv: m.iv, ct: m.ct } as EncBlob);
        const parsed = JSON.parse(plaintext) as { text: string; ts: number };
        resolved = { kind: 'text', valid: true, text: parsed.text };
      } catch {
        resolved = { kind: 'text', valid: true, text: '[failed to decrypt - wrong room key?]' };
      }
    } else {
      resolved = { kind: 'text', valid: true, text: '' };
    }

    m.resolved = resolved;
    return resolved;
  }

  function buildMessageRow(m: LoadedMessage, resolved: Resolved, grouped: boolean): HTMLElement {
    const row = document.createElement('div');
    row.className = 'message' + (resolved.valid ? '' : ' invalid-sig') + (grouped ? ' grouped' : '');
    row.dataset.id = m.id;

    const userEl = document.createElement('div');
    userEl.className = 'message-user';
    userEl.textContent = m.sender; // textContent, not innerHTML - see file header
    row.appendChild(userEl);

    if (resolved.kind === 'image' && resolved.imageUrl) {
      const img = document.createElement('img');
      img.className = 'message-image';
      img.src = resolved.imageUrl; // an object URL we created ourselves from decrypted bytes - see file header
      img.width = resolved.width;
      img.height = resolved.height;
      row.appendChild(img);
    } else {
      const textEl = document.createElement('div');
      textEl.className = 'message-text';
      textEl.textContent = resolved.text ?? ''; // textContent, not innerHTML - see file header
      row.appendChild(textEl);
    }

    return row;
  }

  /** Resolves the signer key once per distinct sender in the batch, before resolving messages in parallel - avoids one PoW-gated REST call per message on a cold room. */
  async function warmSignerKeys(msgs: LoadedMessage[]): Promise<void> {
    const senders = new Set(msgs.map((m) => m.sender));
    await Promise.all([...senders].map((s) => getSignerKey(s)));
  }

  /**
   * True if allMessages[index] immediately follows a message from the same
   * sender - i.e. it should render grouped (no repeated username). Always
   * computed from the true neighbor in allMessages, not from what happens
   * to be in the same rendered batch, so a batch that starts mid-run of one
   * sender's messages (e.g. a page boundary) still groups correctly.
   */
  function isGroupedAt(index: number): boolean {
    return index > 0 && allMessages[index]!.sender === allMessages[index - 1]!.sender;
  }

  /** Builds rows for `msgs`, a contiguous slice of allMessages starting at `startIndex`, and stores each row on its message for later grouping updates (see trimWindow). */
  async function buildFragment(msgs: LoadedMessage[], startIndex: number): Promise<DocumentFragment> {
    await warmSignerKeys(msgs);
    const resolvedList = await Promise.all(msgs.map((m) => resolveMessage(m)));
    const frag = document.createDocumentFragment();
    msgs.forEach((m, i) => {
      const row = buildMessageRow(m, resolvedList[i]!, isGroupedAt(startIndex + i));
      m.row = row;
      frag.appendChild(row);
    });
    return frag;
  }

  /** Trims rendered rows back down to TRIM_TO once MAX_RENDERED is exceeded, discarding from whichever edge is furthest from the edge that was just grown. */
  function trimWindow(grewAt: 'top' | 'bottom'): void {
    const rendered = windowEnd - windowStart;
    if (rendered <= MAX_RENDERED) return;
    const excess = rendered - TRIM_TO;

    if (grewAt === 'top') {
      // Just prepended at the top - the viewport is up there, so discard
      // from the bottom. Those rows are below the viewport; no scroll
      // compensation needed.
      for (let i = 0; i < excess; i++) messagesBox.lastChild?.remove();
      windowEnd -= excess;
    } else {
      // Just appended at the bottom - discard from the top. Those rows are
      // above the viewport, so removing them shrinks scrollHeight above the
      // current scroll position; compensate scrollTop to hold the view.
      const oldScrollHeight = messagesBox.scrollHeight;
      for (let i = 0; i < excess; i++) messagesBox.firstChild?.remove();
      windowStart += excess;
      messagesBox.scrollTop -= oldScrollHeight - messagesBox.scrollHeight;
      // The new first rendered row may have been marked "grouped" (username
      // hidden) because of a predecessor that's now trimmed away - without
      // this it would render as an orphaned message with no visible sender.
      allMessages[windowStart]?.row?.classList.remove('grouped');
    }
  }

  /**
   * Prepends a batch at the top of the DOM, keeping the visible content
   * anchored (no visual jump). `startIndex` is where `msgs` sits in
   * allMessages, i.e. the boundary message that was already rendered right
   * after it - if that boundary message shares a sender with the last
   * prepended message, it's retroactively marked grouped (it wasn't grouped
   * before because its true predecessor hadn't been loaded yet).
   */
  async function prependWithAnchor(msgs: LoadedMessage[], startIndex: number): Promise<void> {
    if (msgs.length === 0) return;
    const frag = await buildFragment(msgs, startIndex);

    const boundary = allMessages[startIndex + msgs.length];
    if (boundary && boundary.sender === msgs[msgs.length - 1]!.sender) {
      boundary.row?.classList.add('grouped');
    }

    const oldScrollHeight = messagesBox.scrollHeight;
    const oldScrollTop = messagesBox.scrollTop;
    messagesBox.insertBefore(frag, messagesBox.firstChild);
    messagesBox.scrollTop = oldScrollTop + (messagesBox.scrollHeight - oldScrollHeight);
  }

  /** Grows the rendered window toward older history: from memory if already fetched, otherwise pages the server with `before`. */
  async function loadOlder(): Promise<void> {
    if (loadingOlder || !state.currentRoom) return;
    loadingOlder = true;
    try {
      let toPrepend: LoadedMessage[];
      let startIndex: number;

      if (windowStart > 0) {
        const newStart = Math.max(0, windowStart - FETCH_LIMIT);
        toPrepend = allMessages.slice(newStart, windowStart);
        startIndex = newStart;
        windowStart = newStart;
      } else {
        if (historyExhausted) return;
        const oldest = allMessages[0];
        const before = oldest ? oldest.serverTs : undefined;
        const { messages } = await api.getHistory(state.currentRoom.roomId, before, FETCH_LIMIT);
        if (messages.length < FETCH_LIMIT) historyExhausted = true;

        const fresh = messages.map(toLoadedMessage).filter((m) => !knownIds.has(m.id));
        if (fresh.length === 0) return;
        for (const m of fresh) knownIds.add(m.id);

        allMessages = [...fresh, ...allMessages];
        windowEnd += fresh.length;
        windowStart = 0;
        toPrepend = fresh;
        startIndex = 0;
      }

      await prependWithAnchor(toPrepend, startIndex);
      trimWindow('top');
    } finally {
      loadingOlder = false;
    }
  }

  /** Grows the rendered window toward newer history - always from memory, since anything newer than what's loaded arrives live via the socket and is appended directly (see handleIncomingMessage). */
  function loadNewer(): void {
    if (loadingNewer || windowEnd >= allMessages.length) return;
    loadingNewer = true;
    const startIndex = windowEnd;
    const newEnd = Math.min(allMessages.length, windowEnd + FETCH_LIMIT);
    const toAppend = allMessages.slice(windowEnd, newEnd);
    windowEnd = newEnd;

    void (async () => {
      try {
        const frag = await buildFragment(toAppend, startIndex);
        messagesBox.appendChild(frag);
        trimWindow('bottom');
      } finally {
        loadingNewer = false;
      }
    })();
  }

  messagesBox.addEventListener('scroll', () => {
    if (messagesBox.scrollTop < SCROLL_TRIGGER_PX) void loadOlder();
    const distanceFromBottom = messagesBox.scrollHeight - messagesBox.scrollTop - messagesBox.clientHeight;
    if (distanceFromBottom < SCROLL_TRIGGER_PX) loadNewer();
  });

  async function openRoom(room: StoredRoom): Promise<void> {
    state.currentRoom = room;
    currentAesKey = await importAesKey(room.h1);
    chatHeaderTitle.textContent = `# ${room.name}`;
    messagesBox.innerHTML = '';
    userDisplay.textContent = state.username ?? '';
    closeDrawer(); // picking a room (from the drawer or the channel list) should reveal the chat
    // The identity panel lives in this screen's sidebar; state.identity is
    // only populated once login/registration completes (see
    // ui/auth.ts and main.ts), which happens after this controller was
    // wired up - refresh here rather than only once at wire time.
    void refreshIdentityPanel();

    allMessages = [];
    knownIds = new Set();
    windowStart = 0;
    windowEnd = 0;
    historyExhausted = false;
    loadingOlder = false;
    loadingNewer = false;

    await renderChannelList(room.roomId);
    state.socket?.subscribeRoom(room.roomId);

    try {
      const { messages } = await api.getHistory(room.roomId, undefined, FETCH_LIMIT);
      if (messages.length < FETCH_LIMIT) historyExhausted = true;

      for (const m of messages.map(toLoadedMessage)) {
        if (knownIds.has(m.id)) continue;
        knownIds.add(m.id);
        allMessages.push(m);
      }
      windowStart = 0;
      windowEnd = allMessages.length;

      const frag = await buildFragment(allMessages, 0);
      messagesBox.appendChild(frag);
      messagesBox.scrollTop = messagesBox.scrollHeight;
    } catch (err) {
      console.error('Failed to load history', err);
    }
  }

  function handleIncomingMessage(m: InboundMsgNew): void {
    if (!state.currentRoom || m.roomId !== state.currentRoom.roomId) return;
    if (knownIds.has(m.id)) return; // guards resubscribe/reconnect redelivery
    knownIds.add(m.id);

    const wasAtNewestEnd = windowEnd === allMessages.length;
    const nearBottom =
      messagesBox.scrollHeight - messagesBox.scrollTop - messagesBox.clientHeight < SCROLL_TRIGGER_PX;

    const startIndex = allMessages.length;
    const loaded = toLoadedMessage(m);
    allMessages.push(loaded);
    if (!wasAtNewestEnd) return; // user has scrolled away from "now" - keep the data, leave the DOM alone

    windowEnd = allMessages.length;
    void (async () => {
      const frag = await buildFragment([loaded], startIndex);
      messagesBox.appendChild(frag);
      trimWindow('bottom');
      if (nearBottom) messagesBox.scrollTop = messagesBox.scrollHeight;
    })();
  }

  function handleWsError(err: InboundError): void {
    console.error(`WS error [${err.code}]: ${err.message}`);
    // Most error codes (bad_request, forbidden, unauthorized, pow_failed,
    // server_error) reflect a client bug rather than something the user did
    // - console logging is enough. message_rejected is the one a real
    // person can actually hit (e.g. a stale/duplicate send), so surface it
    // directly instead of failing silently. There is deliberately no
    // separate rate limit - a solved proof-of-work is the only admission
    // control on sending (see README threat model).
    if (err.code === 'message_rejected') {
      alert(`Message not sent: ${err.message}`);
    }
  }

  messageInput.addEventListener('keydown', (ev) => {
    // keypress is deprecated (and not dispatched by some automation tools);
    // isComposing guards against submitting mid-IME-composition, where Enter
    // confirms a character choice rather than sending the message.
    if (ev.key === 'Enter' && !ev.isComposing) void handleSend();
  });

  async function handleSend(): Promise<void> {
    const text = messageInput.value.trim();
    if (!text || !state.currentRoom || !currentAesKey || !state.identity || !state.username) return;

    const ts = Date.now();
    const { iv, ct } = await aesEncrypt(currentAesKey, JSON.stringify({ text, ts }));
    const kind = 'text' as const;
    const canonical = `${state.currentRoom.roomId}|${state.username}|${ts}|${iv}|${ct}|${kind}`;
    const sig = await signMessage(state.identity.privateKey, canonical);
    const clientRef = crypto.randomUUID();

    messageInput.value = '';
    try {
      await state.socket?.sendMessage({ roomId: state.currentRoom.roomId, kind, iv, ct, ts, sig, clientRef });
    } catch (err) {
      console.error('Failed to send message', err);
    }
  }

  /** Resizes/compresses/encrypts an image client-side, uploads the ciphertext bytes, then sends a signed reference to it - mirrors handleSend()'s shape. */
  async function handleImageFile(file: File): Promise<void> {
    if (!state.currentRoom || !currentAesKey || !state.identity || !state.username) return;
    if (!file.type.startsWith('image/')) {
      alert('Only image files can be attached.');
      return;
    }

    imageProgress.show();
    try {
      const { bytes, width, height } = await prepareImage(file);
      const { iv, ctBytes } = await aesEncryptBytes(currentAesKey, bytes);
      const { imageId } = await api.uploadImage(state.currentRoom.roomId, ctBytes, imageProgress.onProgress);

      const ts = Date.now();
      const kind = 'image' as const;
      const canonical = `${state.currentRoom.roomId}|${state.username}|${ts}|${iv}|${imageId}|${kind}`;
      const sig = await signMessage(state.identity.privateKey, canonical);
      const clientRef = crypto.randomUUID();

      await state.socket?.sendMessage({
        roomId: state.currentRoom.roomId,
        kind,
        iv,
        ct: imageId,
        width,
        height,
        sizeBytes: ctBytes.length,
        ts,
        sig,
        clientRef,
      });
    } catch (err) {
      console.error('Failed to send image', err);
      alert(err instanceof Error ? err.message : 'Failed to send image.');
    } finally {
      imageProgress.hide();
    }
  }

  attachBtn.addEventListener('click', () => attachInput.click());
  attachInput.addEventListener('change', () => {
    const file = attachInput.files?.[0];
    attachInput.value = '';
    if (file) void handleImageFile(file);
  });

  // Drag-and-drop onto the message list. HTML5 drag-and-drop doesn't exist
  // on touch devices at all, which is why the attach button above is the
  // primary path on mobile, not just a fallback.
  messagesBox.addEventListener('dragover', (ev) => ev.preventDefault());
  messagesBox.addEventListener('dragenter', (ev) => {
    ev.preventDefault();
    messagesBox.classList.add('drag-over');
  });
  messagesBox.addEventListener('dragleave', () => messagesBox.classList.remove('drag-over'));
  messagesBox.addEventListener('drop', (ev) => {
    ev.preventDefault();
    messagesBox.classList.remove('drag-over');
    const file = ev.dataTransfer?.files?.[0];
    if (file) void handleImageFile(file);
  });

  backBtn.addEventListener('click', () => {
    state.currentRoom = null;
    closeDrawer(); // reset for the next time this screen is shown
    showRoomsScreen();
    window.dispatchEvent(new CustomEvent('mango:refresh-rooms'));
  });

  logoutBtn.addEventListener('click', () => {
    window.dispatchEvent(new CustomEvent('mango:logout'));
  });

  return { openRoom, handleIncomingMessage, handleWsError };
}
